import React, { useLayoutEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SpeakingBar from './SpeakingBar';
import { StatRow } from './EpisodeStats';
import { episodeUrl } from '../api';

// One colour per stat, so each connector line, arrowhead and badge share a
// hue and you can trace a callout back to its row even where lines run close.
const COLORS = [
  'var(--palette-coral)',
  'var(--palette-violet)',
  'var(--palette-teal)',
  'var(--palette-amber)',
  'var(--palette-sky)',
  'var(--palette-lime)',
  'var(--palette-orange)',
];

const CALLOUTS = [
  {
    title: 'Mic time',
    def: "how the episode's talking time splits between host and guest.",
    lines: [
      'Guest-heavy: the guest carries it.',
      'Host-heavy: the host runs the mic.',
    ],
  },
  {
    title: 'Dialog switches',
    def: 'how often the mic passes from one person to the other.',
    lines: [
      'High: fast back-and-forth, a real conversation.',
      'Low: long uninterrupted stretches, more monologue than dialogue.',
    ],
  },
  {
    title: 'Avg turn',
    def: 'how long each person speaks before the other jumps in, on average.',
    lines: [
      'High: slower rhythm, long-form answers.',
      'Low: quick, punchy exchanges.',
    ],
  },
  {
    title: 'Longest speech',
    def: 'the single longest stretch nobody interrupted.',
    lines: [
      'High: someone went deep, here 14 minutes of an ex-FBI agent telling mob stories.',
      'Low: nobody held the floor for long.',
    ],
  },
  {
    title: 'Host questions',
    def: 'how many questions the host asked.',
    lines: [
      'High: the host is driving the interview.',
      'Low: the host is mostly along for the ride.',
    ],
  },
  {
    title: 'Guest questions',
    def: 'how many questions the guest asked.',
    lines: [
      'Low or zero: the guest is doing the talking, comedians and storytellers land here.',
      'High: a genuine two-way conversation.',
    ],
  },
  {
    title: 'Top words',
    def: 'the words that come up most once the filler is stripped out.',
    lines: [
      'A quick read on what the episode is actually about, before you play it.',
    ],
  },
];

export default function LandingDemo() {
  const containerRef = useRef(null);
  const anchorRefs   = useRef([]);
  const calloutRefs  = useRef([]);
  const [wires, setWires] = useState([]);

  useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    function measure() {
      const cRect = container.getBoundingClientRect();
      const next = [];
      for (let i = 0; i < CALLOUTS.length; i++) {
        const anchor  = anchorRefs.current[i];
        const callout = calloutRefs.current[i];
        const badge   = anchor && anchor.querySelector('.landing-demo__badge');
        if (!badge || !callout) continue;
        const b = badge.getBoundingClientRect();
        const c = callout.getBoundingClientRect();
        const startX = c.left - cRect.left;
        const startY = c.top - cRect.top + c.height / 2;
        const endX   = b.right - cRect.left;
        const endY   = b.top - cRect.top + b.height / 2;
        const bend   = Math.max(40, (startX - endX) / 2);
        next.push({
          d: `M ${startX} ${startY} C ${startX - bend} ${startY}, ${endX + bend} ${endY}, ${endX} ${endY}`,
          startX,
          startY,
          color: COLORS[i],
        });
      }
      setWires(next);
    }

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    window.addEventListener('resize', measure);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);

  const anchor = (n, child) => (
    <div
      className="landing-demo__anchor"
      ref={el => { anchorRefs.current[n - 1] = el; }}
    >
      <span className="landing-demo__badge" style={{ background: COLORS[n - 1] }}>{n}</span>
      {child}
    </div>
  );

  return (
    <div className="landing-demo" ref={containerRef}>
      <svg className="landing-demo__wires" aria-hidden="true">
        <defs>
          {wires.map((w, i) => (
            <marker
              key={i}
              id={`landing-demo-arrow-${i}`}
              viewBox="0 0 10 10"
              refX="8"
              refY="5"
              markerWidth="7"
              markerHeight="7"
              orient="auto-start-reverse"
            >
              <path d="M 0 0 L 10 5 L 0 10 z" style={{ fill: w.color }} />
            </marker>
          ))}
        </defs>
        {wires.map((w, i) => (
          <g key={i}>
            <path d={w.d} markerEnd={`url(#landing-demo-arrow-${i})`} style={{ stroke: w.color }} />
            <circle cx={w.startX} cy={w.startY} r="3.5" style={{ fill: w.color }} />
          </g>
        ))}
      </svg>

      <div className="landing-demo__left">
        <div className="landing-demo__panel">
          {anchor(1,
            <SpeakingBar
              hostPct={12.9}
              guestPct={87.1}
              hostLabel="Host"
              guestLabel="Guest"
              accentColor={COLORS[0]}
            />
          )}
          {anchor(2,
            <StatRow label="Dialog switches" value={448} displayValue={448} max={1000} color={COLORS[1]} />
          )}
          {anchor(3,
            <StatRow label="Avg turn" value={19637} displayValue="20s" max={300000} color={COLORS[2]} />
          )}
          {anchor(4,
            <StatRow label="Longest speech" value={875970} displayValue="14m 36s" max={1800000} color={COLORS[3]} warn />
          )}
          {anchor(5,
            <StatRow label="Host questions" value={90} displayValue={90} max={105} color={COLORS[4]} />
          )}
          {anchor(6,
            <StatRow label="Guest questions" value={15} displayValue={15} max={105} color={COLORS[5]} />
          )}
          {anchor(7,
            <div className="landing-demo__words ep-top-words" style={{ '--tw-color': COLORS[6] }}>
              {[['donnie', 45], ['sonny', 39], ['undercover', 38], ['money', 36], ['lefty', 28]].map(([word, count]) => (
                <span key={word} className="ep-top-word">
                  {word}<span className="ep-top-word__count">{count}</span>
                </span>
              ))}
            </div>
          )}
        </div>
        <p className="landing-demo__caption">
          Real numbers from a real episode:{' '}
          <Link to={episodeUrl('joe-rogan-experience', { title: '#2343 - Joe Pistone' })}>
            JRE #2343, Joe Pistone
          </Link>
        </p>
      </div>

      <ol className="landing-demo__callouts">
        {CALLOUTS.map((c, i) => (
          <li
            key={c.title}
            className="landing-demo__callout"
            ref={el => { calloutRefs.current[i] = el; }}
          >
            <span className="landing-demo__badge" style={{ background: COLORS[i] }}>{i + 1}</span>
            <div className="landing-demo__callout-body">
              <p>
                <strong>{c.title}</strong>: {c.def}
              </p>
              {c.lines.map(line => (
                <span key={line} className="landing-demo__hilo">{line}</span>
              ))}
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}
